/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { includes } from 'lodash';
import { Radio } from '@mui';
import AsyncInputSearch from '@shared/async-input-search';
import '../filter.scss';

const FilterAttrSearch = ({
  attrField,
  dataType,
  enabled,
  filterEnabled,
  filterName,
  placeHolder,
  quickSelect,
  radio,
  setFilters,
  title,
  values
}) => {
  const currentValues = useMemo(() => {
    // Get the current filters for the specified name:
    const current = values[filterName];

    if (R.isNil(current) || R.isEmpty(current)) {
      return [];
    }

    if (Array.isArray(current)) {
      return current;
    }

    return [{
      arg: { filterName: current },
      title: current
    }];
  }, [filterName, values]);

  const onChange = useCallback(suggestions => {
    const newFilter = {
      [filterName]: suggestions.length ? suggestions[0].title : ''
    };
    setFilters(newFilter);
  }, [filterName, setFilters]);

  // We can only render the link if there's quick select data.
  const showQuickSelect = useMemo(() => {
    // Show the quick selection link if the label and value are defined:
    return quickSelect && quickSelect.label && quickSelect.value;
  }, [quickSelect]);

  // Disable the quick select text if the filter is disabled
  // or if the value is already selected.
  const quickSelectEnabled = useMemo(() => {
    return enabled && quickSelect?.value && !includes(currentValues, quickSelect.value);
  }, [currentValues, enabled, quickSelect]);

  const onQuickSelect = useCallback(() => {
    if (!quickSelectEnabled) {
      return;
    }
    setFilters({ [filterName]: [quickSelect.value] });
  }, [filterName, quickSelect, quickSelectEnabled, setFilters]);

  const onRadioChange = useCallback(event => {
    radio.onCheck(event, event.target.value);
  }, [radio]);

  const renderRadio = () => (
    <div styleName="radio">
      <Radio
        disabled={!filterEnabled}
        checked={radio.checked}
        onChange={onRadioChange}
        value={radio.value}
        inputProps={{ 'aria-label': filterName }}
        color="primary"
        style={{ padding: 0, marginRight: '0.5rem' }}
      />
    </div>
  );

  const toOptions = useCallback(results => results.suggestions.map(
    ({
      arg,
      display,
      extra,
      id,
      icon,
      type,
      type_label
    }) => ({
      arg,
      id,
      icon,
      extra,
      title: display,
      type,
      type_label,
      value: `${type}-${id}`
    })
  ), []);

  // If there's data use a space to make the TextField component
  // think there's a label, thus making it reserve a 16px top margin.
  const label = R.isEmpty(currentValues) ? placeHolder : ' ';

  return (
    <div styleName="content-filter-group">
      {title && (
        <div styleName="title">
          {radio && renderRadio()}
          <div styleName="label">{title}</div>
        </div>
      )}
      <div styleName="list">
        <AsyncInputSearch
          avatarProps={{ type: 'star', value: quickSelect ? quickSelect.value : null }}
          fieldName={filterName}
          dataType={dataType}
          disabled={!enabled}
          onChange={onChange}
          label={label}
          value={currentValues}
          toOptions={toOptions}
          attrField={attrField}
          autocompleteContainerStyles={{ maxWidth: '16rem' }}
        />
      </div>
      {showQuickSelect && (
        <div
          role="presentation"
          onClick={onQuickSelect}
          styleName={`quick-select-link ${quickSelectEnabled ? '' : 'quick-disabled'}`}
          aria-hidden={!quickSelectEnabled}
        >
          Select <span styleName="quick-select-name">{quickSelect.label}</span>
        </div>
      )}
    </div>
  );
};

FilterAttrSearch.propTypes = {
  attrField: PropTypes.string,
  dataType: PropTypes.string,
  enabled: PropTypes.bool,
  filterEnabled: PropTypes.bool,
  filterName: PropTypes.string,
  placeHolder: PropTypes.string,
  quickSelect: PropTypes.object,
  radio: PropTypes.object,
  setFilters: PropTypes.func,
  title: PropTypes.string,
  values: PropTypes.object
};

export default memo(FilterAttrSearch);
